// Živé tahy: jedno SSE spojení na tah, stav sdílený přes useSyncExternalStore.
// Po dokončení se výsledek zapíše do cache konverzace a živý stav se zahodí.
import { useSyncExternalStore } from 'react';
import type { ConversationDetailDTO, MessageDTO, Step, TurnEvent, TurnPhase } from '@sam/shared';
import { keys, queryClient } from './queries';

export interface LiveTurn {
  turnId: string;
  conversationId: string;
  messageId: string;
  phase: TurnPhase;
  steps: Step[];
  text: string;
}

const live = new Map<string, LiveTurn>(); // podle messageId
const sources = new Map<string, EventSource>(); // podle turnId
const listeners = new Set<() => void>();
/** Zprávy, které se v tomhle okně streamovaly, se po dokončení dovypisují (ostatní se ukážou celé). */
export const animatedMessages = new Set<string>();

function emit() {
  for (const l of listeners) l();
}

function patch(messageId: string, fn: (t: LiveTurn) => LiveTurn) {
  const t = live.get(messageId);
  if (!t) return;
  live.set(messageId, fn(t));
  emit();
}

function finish(t: LiveTurn, message: MessageDTO) {
  sources.get(t.turnId)?.close();
  sources.delete(t.turnId);
  queryClient.setQueryData<ConversationDetailDTO>(keys.conversation(t.conversationId), (old) =>
    old ? { ...old, activeTurnId: null, messages: old.messages.map((m) => (m.id === message.id ? message : m)) } : old,
  );
  live.delete(t.messageId);
  emit();
  void queryClient.invalidateQueries({ queryKey: keys.conversations });
}

/** Napojí se na běžící tah. Volat opakovaně je bezpečné. */
export function followTurn(p: { turnId: string; conversationId: string; messageId: string }) {
  if (sources.has(p.turnId)) return;
  animatedMessages.add(p.messageId);
  if (!live.has(p.messageId)) {
    live.set(p.messageId, { ...p, phase: 'thinking', steps: [], text: '' });
    emit();
  }
  const es = new EventSource(`/api/turns/${encodeURIComponent(p.turnId)}/stream`);
  sources.set(p.turnId, es);
  let firstReplay = true;
  es.onopen = () => {
    // Po reconnectu server přehraje tah od začátku: začít s čistým stavem.
    if (!firstReplay) patch(p.messageId, (t) => ({ ...t, steps: [], text: '' }));
    firstReplay = false;
  };
  es.onmessage = (ev) => {
    const e = JSON.parse(ev.data) as TurnEvent;
    const t = live.get(p.messageId);
    if (!t) return;
    switch (e.type) {
      case 'phase':
        patch(p.messageId, (x) => ({ ...x, phase: e.phase }));
        break;
      case 'step':
        patch(p.messageId, (x) => {
          const i = x.steps.findIndex((s) => s.id === e.step.id);
          const steps = i < 0 ? [...x.steps, e.step] : x.steps.map((s, k) => (k === i ? e.step : s));
          return { ...x, steps };
        });
        break;
      case 'text':
        patch(p.messageId, (x) => ({ ...x, text: e.replace ? e.delta : x.text + e.delta }));
        break;
      case 'done':
      case 'error':
        finish(t, e.message);
        // Po první odpovědi Sam navrhne název chatu (chvíli trvá): dočíst seznam chatů dvakrát.
        for (const ms of [4000, 12000]) setTimeout(() => void queryClient.invalidateQueries({ queryKey: keys.conversations }), ms);
        break;
    }
  };
  es.onerror = () => {
    // EventSource se sám znovu připojí. Když tah mezitím zmizel (404), dočteme konverzaci.
    if (es.readyState === EventSource.CLOSED) {
      sources.delete(p.turnId);
      live.delete(p.messageId);
      emit();
      void queryClient.invalidateQueries({ queryKey: keys.conversation(p.conversationId) });
    }
  };
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export function useLiveTurn(messageId: string): LiveTurn | undefined {
  return useSyncExternalStore(subscribe, () => live.get(messageId));
}

export function useAnyLiveTurn(conversationId: string): boolean {
  return useSyncExternalStore(subscribe, () => {
    for (const t of live.values()) if (t.conversationId === conversationId) return true;
    return false;
  });
}
